import React, { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useListTasks, Task } from '../../api/tasks';
import { DEFAULT_TASK_TYPE } from './utils';
import { TaskCard } from './TaskCard';
import { LoadingState } from '../../components/common/LoadingState';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { PleriClient } from '../../components/Pleri/client/PleriClient';
import {
  libraryItems,
  LibraryItem,
} from '../../components/Pleri/components/Library/libraryData';
import { useUserProfile } from '../../api/profile';
import { useEligibleForCodeFixTaskForGithubAndNode } from '../../hooks/useEligibleForCodeFixTaskForGithubAndNode';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

// Configurable headings linked to task IDs; the greeting carries the user's name
const taskHeadings = (t: TFunction<'pleri/pages'>, greeting: string) => [
  {
    text: t('heyLetCreateATaskToFix', { greeting }),
    taskId: 'fix-js-package-vulnerabilities',
  },
  {
    text: t('heyDoYouWantToCheckCompliance', { greeting }),
    taskId: 'compliance-daily-check',
  },
  {
    text: t('heyWantMeToHelpYouCreate', { greeting }),
    taskId: 'weekly-cloud-check',
  },
  {
    text: t('heyNeedHelpSettingUpAMonitoring', { greeting }),
    taskId: 'weekly-mfa-check',
  },
  {
    text: t('heyWantToAutomateASecurityCheck', { greeting }),
    taskId: 'iam-user-review',
  },
];

export const TaskListPage: React.FC = () => {
  const { t: tLibrary } = useTranslation('pleri/library');
  const { t } = useTranslation('pleri/pages');
  const navigate = useNavigate();
  const { data: userProfile } = useUserProfile();
  const {
    isEligible: eligibleForCodeFixTaskForGithubAndNode,
    isLoading: isLoadingEligibility,
  } = useEligibleForCodeFixTaskForGithubAndNode();

  const {
    data: tasks,
    isLoading,
    isFetching,
    refetch,
  } = useListTasks(
    { scope: DEFAULT_TASK_TYPE },
    {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      staleTime: 0,
    },
  );

  // Filter library items for task examples
  const taskItems = libraryItems(tLibrary).filter(
    (item) => item.chatPageCategory === 'task',
  );

  // Only show Node.js heading if GitHub integration exists and node repositories are present
  const greeting = userProfile?.name
    ? t('heyName', { name: userProfile.name })
    : t('hey');

  const selectedHeading = useMemo(() => {
    if (eligibleForCodeFixTaskForGithubAndNode) {
      return (
        taskHeadings(t, greeting).find(
          (heading) => heading.taskId === 'fix-js-package-vulnerabilities',
        ) || taskHeadings(t, greeting)[0]
      );
    }
    // Return a different heading if conditions are not met
    return (
      taskHeadings(t, greeting).find(
        (heading) => heading.taskId === 'compliance-daily-check',
      ) || taskHeadings(t, greeting)[0]
    );
  }, [eligibleForCodeFixTaskForGithubAndNode, t, greeting]);

  // Get the matching task for the selected heading
  // First try to find in taskItems, if not found, search in all libraryItems
  const matchingTask = useMemo(() => {
    if (!selectedHeading) {
      return undefined;
    }
    return (
      taskItems.find((item) => item.id === selectedHeading.taskId) ||
      libraryItems(tLibrary).find((item) => item.id === selectedHeading.taskId)
    );
  }, [selectedHeading, taskItems]);

  // Sort tasks: matching task first, then others
  // Only include Node.js task if GitHub integration exists and node repositories are present
  const sortedTaskItems = useMemo(() => {
    if (!selectedHeading) {
      return taskItems.slice(0, 6);
    }

    // Filter out Node.js task if conditions are not met
    const filteredTaskItems = eligibleForCodeFixTaskForGithubAndNode
      ? taskItems
      : taskItems.filter(
          (item) => item.id !== 'fix-js-package-vulnerabilities',
        );

    if (matchingTask) {
      // Ensure matching task is in filteredTaskItems for display, then place it first
      const otherTasks = filteredTaskItems.filter(
        (item) => item.id !== selectedHeading.taskId,
      );
      // If matching task is not in filteredTaskItems, add it; otherwise use the one from filteredTaskItems
      const taskToShow =
        filteredTaskItems.find((item) => item.id === selectedHeading.taskId) ||
        matchingTask;
      return [taskToShow, ...otherTasks].slice(0, 6);
    }
    return filteredTaskItems.slice(0, 6);
  }, [
    matchingTask,
    taskItems,
    selectedHeading,
    eligibleForCodeFixTaskForGithubAndNode,
  ]);

  const handleTaskClick = (task: Task) => {
    navigate({
      to: '/tasks/$taskId',
      params: { taskId: task.id },
    });
  };

  if (isLoading || isLoadingEligibility) {
    return (
      <div className="flex-1 p-6">
        <LoadingState text={t('loadingTasks')} />
      </div>
    );
  }

  const hasTasks = tasks && tasks.length > 0;
  const taskCount = tasks?.length || 0;

  const handleExampleTaskClick = (item: LibraryItem) => {
    // For other tasks, use the existing chat input flow
    PleriClient.updateChatInput({
      prompt: item.prompt,
      autoSubmit: item.autoSubmit,
      metrics: {
        eventLocation: 'tasks-list',
        eventName: 'libraryItemClick',
        eventType: item.autoSubmit ? 'autoSubmitQuery' : 'templateUse',
        eventValue: item.id,
        eventDetails: {
          category: item.category,
          title: item.title,
          tags: item.tags,
        },
      },
    });
  };

  return (
    <div className="flex-1 bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
                {t('tasks')}
              </h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {t('taskCount', { count: taskCount })}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
                title={t('refreshTasks')}
              >
                <ArrowPathIcon
                  className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
                />
                {t('refresh')}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Current Tasks Section - At the top */}
      <div className="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-6 pb-3">
          {hasTasks ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {tasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onClick={() => handleTaskClick(task)}
                />
              ))}
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
      {/* Example Tasks Section */}
      {sortedTaskItems.length > 0 && (
        <div className="bg-white dark:bg-gray-900">
          <div className="mx-auto max-w-7xl px-6 py-4">
            {/* Header */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {selectedHeading
                  ? selectedHeading.text
                  : t('getStartedWithTasks')}
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t('clickOnAnyExampleBelowToGet')}
              </p>
            </div>

            {/* Example Tasks Grid */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {sortedTaskItems.map((item) => {
                const isMatchingTask =
                  selectedHeading && item.id === selectedHeading.taskId;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleExampleTaskClick(item)}
                    className={`group relative flex w-full flex-col rounded-2xl border p-5 text-left shadow-sm transition-all duration-150 ${
                      isMatchingTask
                        ? 'border-orange-200 bg-orange-50/50 hover:border-orange-300 hover:shadow-md dark:border-gray-600 dark:bg-gray-800'
                        : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-md dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div
                        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-gray-700 transition-colors dark:text-gray-300 ${
                          isMatchingTask
                            ? 'bg-orange-100 text-orange-600 group-hover:bg-orange-200 dark:bg-gray-700 dark:text-gray-300'
                            : 'bg-gray-50 group-hover:bg-orange-50 group-hover:text-orange-600 dark:bg-gray-800 dark:group-hover:bg-gray-700 dark:group-hover:text-gray-200'
                        }`}
                      >
                        {item.icon}
                      </div>
                      {!item.autoSubmit && (
                        <span className="flex-shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-950">
                          {t('template')}
                        </span>
                      )}
                    </div>
                    <div className="mt-4">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        {item.description}
                      </p>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-1">
                      {item.tags.slice(0, 3).map((tag: string) => (
                        <span
                          key={tag}
                          className="inline-flex items-center rounded-md bg-gray-50 px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
